import { logger } from '@/lib/utils/logger';
import { readStoredLanguage } from '@/shared/api/user-preferences.api';
import i18n, { loadLocale } from './config';
import { readCachedLocale } from './locale-cache';
import { FALLBACK_LOCALE, resolveLocale } from './locale-resolution';

/**
 * Never rejects. The boot path gates the render on this promise, so a locale
 * that cannot be applied has to leave the app in English rather than unrendered.
 *
 * Belongs in that boot path rather than in a component: it is the window the app
 * already spends waiting on `/auth/me`, which is what the preference rides on.
 */
export async function applyInitialLocale(user: unknown, enabled: boolean): Promise<string> {
  if (!enabled) return i18n.language;

  const locale = resolveLocale({
    stored: readStoredLanguage(user),
    cached: readCachedLocale(),
    preferred: navigator.languages,
  });

  try {
    if (locale !== i18n.language) {
      // Fetch before switching. Switching first would render the resolved locale
      // against an empty bundle, so the interface would flash English on a path
      // whose whole purpose is to avoid that.
      await loadLocale(locale);
      await i18n.changeLanguage(locale);
    }
    return locale;
  } catch (error) {
    logger.error('Failed to apply the initial locale:', error);
    return FALLBACK_LOCALE;
  }
}
